import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Shield, Plus, Edit3, Trash2, RefreshCw, AlertCircle, CheckCircle,
  Loader2, Users, X, Search,
} from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

interface Permission { code: string; description: string; }

interface CustomRole {
  id: number;
  role_code: string;
  display_name: string;
  description: string;
  base_role: string | null;
  permissions: string[];
  active: boolean;
  users_assigned: number;
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
}

interface ListResp { total: number; roles: CustomRole[]; }
interface PermsResp { permissions: Permission[]; }

const authHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const emptyForm = {
  id: 0,
  role_code: "",
  display_name: "",
  description: "",
  base_role: "" as string,
  permissions: [] as string[],
  active: true,
};

const CustomRoles: React.FC = () => {
  const [roles, setRoles] = useState<CustomRole[]>([]);
  const [perms, setPerms] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CustomRole | null>(null);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<CustomRole | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const [rl, pr] = await Promise.all([
        axios.get<ListResp>(`${API_URL}/settings/roles`, { headers: authHeaders() }),
        axios.get<PermsResp>(`${API_URL}/settings/roles/permissions`, { headers: authHeaders() }),
      ]);
      setRoles(rl.data.roles || []);
      setPerms(pr.data.permissions || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load custom roles");
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetchAll(false); }, [fetchAll]);

  const showToast = (kind: "ok" | "err", msg: string) => {
    if (kind === "ok") setNotice(msg); else setError(msg);
    setTimeout(() => { if (kind === "ok") setNotice(null); else setError(null); }, 3500);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (r: CustomRole) => {
    setEditing(r);
    setForm({
      id: r.id,
      role_code: r.role_code,
      display_name: r.display_name,
      description: r.description || "",
      base_role: r.base_role || "",
      permissions: [...r.permissions],
      active: r.active,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const togglePermission = (code: string) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(code)
        ? f.permissions.filter(p => p !== code)
        : [...f.permissions, code],
    }));
  };

  const submitForm = async () => {
    setFormError(null);
    if (!form.role_code.trim()) { setFormError("Role code is required"); return; }
    if (!/^[a-z0-9_]{2,64}$/.test(form.role_code)) { setFormError("role_code: 2–64 chars, lowercase letters/digits/underscore only"); return; }
    if (!form.display_name.trim()) { setFormError("Display name is required"); return; }
    if (form.permissions.length === 0) { setFormError("Select at least one permission"); return; }
    setSaving(true);
    const body = {
      role_code: form.role_code.trim(),
      display_name: form.display_name.trim(),
      description: form.description.trim() || null,
      base_role: form.base_role || null,
      permissions: form.permissions,
      active: form.active,
    };
    try {
      if (editing) {
        await axios.patch(`${API_URL}/settings/roles/${editing.id}`, body, { headers: authHeaders() });
        showToast("ok", `Role "${body.display_name}" updated`);
      } else {
        await axios.post(`${API_URL}/settings/roles`, body, { headers: authHeaders() });
        showToast("ok", `Role "${body.display_name}" created`);
      }
      setModalOpen(false);
      fetchAll(true);
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || e?.message || "Failed to save role");
    } finally { setSaving(false); }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/settings/roles/${deleteTarget.id}`, { headers: authHeaders() });
      showToast("ok", `Role "${deleteTarget.display_name}" deactivated`);
      setDeleteTarget(null);
      fetchAll(true);
    } catch (e: any) {
      showToast("err", e?.response?.data?.detail || e?.message || "Failed to deactivate role");
    } finally { setDeleting(false); }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return roles;
    return roles.filter(r =>
      r.role_code.toLowerCase().includes(s) ||
      r.display_name.toLowerCase().includes(s) ||
      (r.description || "").toLowerCase().includes(s));
  }, [roles, search]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-2xl p-7 text-white shadow-xl shadow-amber-500/30"
        style={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 55%, #b45309 100%)" }}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full blur-3xl bg-orange-300/35" />
        <div className="pointer-events-none absolute -left-10 bottom-0 w-48 h-48 rounded-full blur-3xl bg-yellow-300/25" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 shadow-lg shadow-black/10">
              <Shield className="w-7 h-7 text-white" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 mb-2 ring-1 ring-white/20">
                <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                <span className="text-[11px] font-semibold text-white/90 tracking-wide uppercase">Permission grid</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Custom roles</h1>
              <p className="text-sm text-white/85 mt-1.5 max-w-2xl leading-relaxed">
                Define your own role catalog with a precise permissions grid.
                Assign a custom role to any team member from the Team page — it overrides the built-in role.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchAll(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-white/15 backdrop-blur-sm border border-white/25 text-white text-sm font-semibold hover:bg-white/25 disabled:opacity-50 transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-amber-700 text-sm font-semibold shadow-lg shadow-black/10 hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              <Plus className="w-4 h-4" />
              New role
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
        </div>
      )}
      {notice && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5" /> {notice}
        </div>
      )}

      {/* Search + table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by code, name, description…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">
            {roles.length === 0
              ? "No custom roles yet. Click New role to define one."
              : "No roles match this search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Role</th>
                  <th className="px-4 py-3 text-left font-semibold">Permissions</th>
                  <th className="px-4 py-3 text-left font-semibold">Base</th>
                  <th className="px-4 py-3 text-left font-semibold">Users</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-medium">{r.display_name}</div>
                      <div className="text-[11px] font-mono text-slate-500 mt-0.5">{r.role_code}</div>
                      {r.description && (
                        <div className="text-xs text-slate-500 mt-1 max-w-[280px]">{r.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[320px]">
                        {r.permissions.slice(0, 6).map(p => (
                          <span key={p} className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200">
                            {p}
                          </span>
                        ))}
                        {r.permissions.length > 6 && (
                          <span className="text-[10px] text-slate-500">+{r.permissions.length - 6} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.base_role
                        ? <span className="text-xs font-mono text-slate-600">{r.base_role}</span>
                        : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-sm text-slate-700">
                        <Users className="w-3 h-3 text-slate-500" /> {r.users_assigned}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                          <CheckCircle className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => openEdit(r)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-200 text-slate-700 hover:bg-slate-50">
                          <Edit3 className="w-3 h-3" /> Edit
                        </button>
                        {r.active && (
                          <button onClick={() => setDeleteTarget(r)}
                            disabled={r.users_assigned > 0}
                            title={r.users_assigned > 0 ? `Cannot deactivate — ${r.users_assigned} user(s) assigned` : "Deactivate"}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed">
                            <Trash2 className="w-3 h-3" /> Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">
                {editing ? "Edit custom role" : "New custom role"}
              </h2>
              <button onClick={() => setModalOpen(false)} className="p-1 rounded hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Role code *</label>
                  <input type="text" value={form.role_code}
                    onChange={e => setForm(f => ({ ...f, role_code: e.target.value.toLowerCase() }))}
                    placeholder="ops_analyst"
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono outline-none focus:ring-2 focus:ring-emerald-500"
                    disabled={!!editing}
                    title={editing ? "Rename requires unassigning all users first" : ""} />
                  <p className="text-[11px] text-slate-400 mt-1">Machine key. Lowercase letters, digits, underscore only.</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Display name *</label>
                  <input type="text" value={form.display_name}
                    onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                    placeholder="Ops Analyst" maxLength={128}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Description</label>
                <input type="text" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional — what this role is for" maxLength={500}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Base role (inheritance, optional)</label>
                <select value={form.base_role}
                  onChange={e => setForm(f => ({ ...f, base_role: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:ring-2 focus:ring-emerald-500">
                  <option value="">— none —</option>
                  <option value="merchant_admin">merchant_admin</option>
                  <option value="merchant_finance">merchant_finance</option>
                  <option value="merchant_support">merchant_support</option>
                  <option value="merchant_integration">merchant_integration</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Informational only. Permissions must still be picked explicitly below.</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-slate-600">Permissions *</label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setForm(f => ({ ...f, permissions: perms.map(p => p.code) }))}
                      className="text-xs text-emerald-700 hover:underline">Select all</button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={() => setForm(f => ({ ...f, permissions: [] }))}
                      className="text-xs text-slate-500 hover:underline">Clear</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto border border-slate-100 rounded-lg p-3">
                  {perms.map(p => (
                    <label key={p.code} className="flex items-start gap-2 py-1 px-1.5 rounded hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={form.permissions.includes(p.code)}
                        onChange={() => togglePermission(p.code)}
                        className="mt-0.5" />
                      <div className="flex-1">
                        <div className="text-xs font-mono text-slate-800">{p.code}</div>
                        <div className="text-[11px] text-slate-500">{p.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">{form.permissions.length} permission(s) selected.</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                Active (users can be assigned)
              </label>
              {formError && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-2.5 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5" /> {formError}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={submitForm} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? "Save changes" : "Create role"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Deactivate custom role</h2>
            <p className="text-sm text-slate-600">
              Are you sure you want to deactivate <span className="font-semibold">{deleteTarget.display_name}</span> ({deleteTarget.role_code})?
              The role stays in history for audit but no user can be assigned to it anymore.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Keep it</button>
              <button onClick={submitDelete} disabled={deleting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50">
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomRoles;
